import {Button, Drawer, DrawerProps, message, Space, Spin} from 'antd';
import React, {useRef} from 'react';
import TableRender, {ProColumnsType, TableContext} from 'table-render';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import useTable from 'Utils/Hooks/useTable';
import Style from './index.module.scss';
import {PlusOutlined} from '@ant-design/icons';
import useModalHandler from 'Utils/Hooks/useModalHandler';
import EditModal from './Components/EditModal';
import {errorMessage} from 'Utils/common';
import DeleteConfirm from 'Components/DeleteConfirm';
import {useDict} from '../../../../store';
import {deleteLocate, getLocatePage, LocateList} from './service';

interface Props extends DrawerProps {
    onOk: () => void;
    catid: string;
}

const Index = ({onOk, catid, ...props}: Props) => {
    const dict = useDict();
    const {tableInit} = useTable(getLocatePage, {data: {catid}});
    const tableRef = useRef<TableContext>(null);
    const editModal = useModalHandler({
        component: EditModal,
    });

    const [deleteAction, {loading: deleteLoading}] = useRequestFn(deleteLocate);

    function reload() {
        tableRef?.current?.refresh();
    }

    async function onDelete(id: string) {
        try {
            await deleteAction(id);
            await reload();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const columns: ProColumnsType<LocateList> = [
        {
            title: '定位名称',
            dataIndex: 'positionName',
        },
        {
            title: '定位类型',
            dataIndex: 'positionType',
            width: 160,
            render: dict.PositionType.getName,
        },
        {
            title: '定位Xpath',
            dataIndex: 'positionXpath',
        },
        {
            title: 'text和其他属性',
            width: 200,
            dataIndex: 'valuePosition',
        },
        {
            title: '操作',
            fixed: 'right',
            dataIndex: 'id',
            width: 160,
            align: 'center',
            render: (id: string, data) => (
                <Space>
                    {data.parentId === 0 && (
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                editModal.open({
                                    parentId: parseInt(data.id),
                                    catid,
                                    onOk: () => {
                                        editModal.close();
                                        reload();
                                    },
                                });
                            }}>
                            增加下级
                        </Button>
                    )}

                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            editModal.open({
                                parentId: data.parentId,
                                data: data,
                                catid,
                                onOk: () => {
                                    editModal.close();
                                    reload();
                                },
                            });
                        }}>
                        修改
                    </Button>
                    <DeleteConfirm onSuccess={() => onDelete(id)}>
                        <Button size="small" type="link">
                            删除
                        </Button>
                    </DeleteConfirm>
                </Space>
            ),
        },
    ];

    const toolBar = (
        <div className={Style.toolBar}>
            <Space>
                <Button
                    type="primary"
                    onClick={() =>
                        editModal.open({
                            catid,
                            parentId: 0,
                            onOk: () => {
                                editModal.close();
                                reload();
                            },
                        })
                    }
                    icon={<PlusOutlined />}>
                    新增
                </Button>
            </Space>
        </div>
    );

    return (
        <Drawer
            title="定位设置"
            className={Style.root}
            {...props}
            onClose={onOk}
            width="90%">
            <Spin spinning={deleteLoading}>
                <TableRender
                    expandable={{
                        defaultExpandAllRows: true,
                    }}
                    rowKey="id"
                    ref={tableRef}
                    columns={columns}
                    toolbarRender={toolBar}
                    scroll={{x: 'max-content'}}
                    {...tableInit()}
                />
            </Spin>
            {editModal.render()}
        </Drawer>
    );
};

export default Index;
